import { toast } from '@heidi/design-system/toast';
import { GET_SESSION_BY_ID, type QuerySessionResponse } from '@heidi/session/contracts';
import { useWorkspaceId } from '@heidi/workspace/data';
import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { getWorkHostPort } from '../../work-home/work-home-ports';

import {
  isSilentTurnAbandonment,
  type TurnOperation,
} from '../../../contracts/turn-identity';
import type { WorkLaunchAnalyticsProps } from '../../../contracts/work-launch-analytics.types';
import { getSubmitTurnErrorMessage } from '../../../data/projects/components/ProjectChat.helpers';
import {
  notePushApplyEpochAtom,
  notePushApplyStatusAtom,
  projectChatNotePushApplyAtom,
} from '../../../data/projects/state/note-push-apply-bridge';
import { extractNotePushPlanConflict } from '../../../data/projects/state/note-push-conflict-codes';
import { buildNotePushPlan } from '../../../data/projects/state/note-push-plan';
import type { SubmitProjectTurnArgs } from '../../../data/projects/submit-project-turn-args';

/**
 * Turn prompts for the two panel-driven note-push affordances. Agent-facing
 * instructions (not decorative UI copy) — the backend reads the structured
 * `note_push_plan` for the apply path; the prompt is the transcript label.
 */
const APPLY_TURN_TEXT = 'Apply the confirmed note-field mapping to the chart.';
const DISCOVER_TURN_TEXT =
  'Find the on-screen fields for the note sections that still need a target.';

const applyErrorMessages = defineMessages({
  submitFailedTitle: {
    defaultMessage: 'Couldn’t apply the mapping',
    id: '82vL8X0BVd',
    description:
      'Toast title when submitting the confirmed note-push mapping fails for a generic reason',
  },
});

/**
 * What the app resolves for the apply path: the run's flow identity and the note
 * view's revision, the note re-fetch and the apply emit.
 */
export interface PublishNotePushApplyHost {
  /** The ambient workspace the plan is built under. */
  workspaceId: string | null;
  /**
   * The note session from the copilot escalation's projectId→sessionId map (the
   * note-push panel always lives in an escalated run).
   */
  sessionId: string | null;
  /**
   * The note view's `updated_at`, from a read-only observer over the session view
   * the main window already fetches; `undefined` until that view is cached.
   */
  noteRevision: string | null | undefined;
  /** Re-fetch the note view after a stale-revision 409. */
  onStaleNoteRevision: (sessionId: string) => void;
  /**
   * `work_artifact_actioned` for an accepted apply. The hook decides the payload;
   * the app adds the billing columns (module-level).
   */
  trackArtifactApplied: (props: WorkLaunchAnalyticsProps) => void;
}

/**
 * Publish the {@link projectChatNotePushApplyAtom} bridge while the chat is
 * mounted, so the verification panel can route its CONFIRMED mapping into a real
 * turn (the structured handoff). Flow identity — workspace and the escalated
 * run's session — is resolved by the host, so the panel stays presentation +
 * gating only. Cleared on unmount so a stale submit can't fire. Preferring
 * `ProjectDetail.session_id` for manually-opened runs is a follow-up.
 *
 * Also owns the plan's optimistic-concurrency binding and the note-push 409s:
 * `note_revision` is the note view's `updated_at` echoed verbatim at plan-build
 * time, and the two machine-readable conflicts drive the panel status —
 * `stale_note_revision` re-fetches the note (never retries the stale plan),
 * `all_fields_already_placed` lands the terminal push-complete state.
 */
export const usePublishNotePushApplyCore = (
  params: {
    enqueueTurn: (command: SubmitProjectTurnArgs) => TurnOperation;
    projectId: string;
    /**
     * The transport's submit pipeline is in flight (attachment upload + turn POST)
     * — gates the panel's Confirm during the POST.
     */
    isSubmitting: boolean;
    /**
     * A turn is active on the run — stays true PAST POST acceptance, until the
     * agent run/approval settles. `isSubmitting` clears at POST-accept, so without
     * this the footer would re-enable Confirm/Discover mid-apply and let a second
     * identical `note_push_plan` be submitted before the first write settles.
     */
    turnInFlight: boolean;
  },
  host: PublishNotePushApplyHost
): void => {
  const { enqueueTurn, projectId, isSubmitting, turnInFlight } = params;
  const {
    workspaceId,
    sessionId,
    noteRevision,
    onStaleNoteRevision,
    trackArtifactApplied,
  } = host;
  const intl = useIntl();
  const setBridge = useSetAtom(projectChatNotePushApplyAtom);
  const setApplyStatus = useSetAtom(notePushApplyStatusAtom);
  // Liveness epoch mirrored into a ref (the standard "latest value" pattern), so
  // an apply's async onError can compare the epoch it SUBMITTED under against the
  // current one and drop a late outcome after the panel closed (which bumps it).
  const applyEpoch = useAtomValue(notePushApplyEpochAtom);
  const applyEpochRef = useRef(applyEpoch);
  applyEpochRef.current = applyEpoch;

  const ready = Boolean(projectId && workspaceId && sessionId);

  useEffect(() => {
    if (!projectId) {
      setBridge(null);
      return;
    }
    setBridge({
      ready,
      // Disabled for the WHOLE active turn, not just the POST: a note-push apply
      // holds the run until the agent write/approval settles.
      submitting: isSubmitting || turnInFlight,
      applyMapping: (mapping) => {
        if (!workspaceId || !sessionId) return;
        const plan = buildNotePushPlan(mapping, {
          workspaceId,
          sessionId,
          noteRevision: noteRevision ?? null,
          // A first panel submit places everything; threading the already-verified
          // selectors on a re-submit after a partial is a follow-up.
          alreadyPlaced: [],
        });
        if (!plan) return; // incomplete — the panel gates this, defence-in-depth
        // The panel session this apply belongs to. If the panel closes/switches
        // (bumping the epoch) before the turn 409s, the late outcome below is
        // dropped — it must not write a stale status the next panel opens into.
        const epochAtSubmit = applyEpochRef.current;
        // A fresh apply clears any prior outcome so the panel never shows a
        // stale conflict against the new attempt.
        setApplyStatus('idle');
        void enqueueTurn({
          text: APPLY_TURN_TEXT,
          notePushPlan: plan,
          failureUxOwner: 'caller',
        })
          .accepted.then(() => {
            if (applyEpochRef.current !== epochAtSubmit) return;
            trackArtifactApplied({
              action: 'applied',
              object_type: 'artifact',
              artifact_type: 'note-push-verification',
              surface: 'project_chat',
              project_id: projectId,
              outcome: 'succeeded',
            });
          })
          .catch((error: unknown) => {
            // The panel that started this apply is gone (closed/switched) — its
            // status was already reset on unmount; do NOT resurrect it.
            if (applyEpochRef.current !== epochAtSubmit) return;
            if (isSilentTurnAbandonment(error)) return;
            const conflict = extractNotePushPlanConflict(error);
            if (conflict === 'stale_note_revision') {
              // The note changed under the user. Surface it, and re-fetch so
              // the panel + the NEXT confirm bind the current note and its
              // revision — the stale plan itself is never silently retried.
              setApplyStatus('stale-note-revision');
              onStaleNoteRevision(sessionId);
              return;
            }
            if (conflict === 'all_fields_already_placed') {
              // Everything the plan named is already verified-placed —
              // push-complete, not a failure. Terminal; no re-write.
              setApplyStatus('already-placed');
              return;
            }
            if (conflict === 'identity_mismatch') {
              // The plan's workspace/session no longer matches the run.
              // Surface distinctly (no toast) — re-confirm against this run.
              setApplyStatus('identity-mismatch');
              return;
            }
            if (conflict === 'incomplete_route_to_discovery') {
              // 422: the plan is incomplete. Route to discovery, clear the
              // stale preview, and DON'T toast (a routing signal, not an error).
              // The follow-up discover turn has its own failure transition: if it
              // fails, leave the routing state and surface the generic error so
              // the panel doesn't stick on "finding fields" forever.
              setApplyStatus('route-to-discovery');
              void enqueueTurn({
                text: DISCOVER_TURN_TEXT,
                failureUxOwner: 'caller',
              }).accepted.catch((discoverError: unknown) => {
                if (applyEpochRef.current !== epochAtSubmit) return;
                if (isSilentTurnAbandonment(discoverError)) return;
                setApplyStatus('idle');
                toast({
                  title: intl.formatMessage(applyErrorMessages.submitFailedTitle),
                  description: getSubmitTurnErrorMessage(discoverError, intl),
                  tone: 'destructive',
                });
              });
              return;
            }
            toast({
              title: intl.formatMessage(applyErrorMessages.submitFailedTitle),
              description: getSubmitTurnErrorMessage(error, intl),
              tone: 'destructive',
            });
          });
      },
      discover: () =>
        void enqueueTurn({
          text: DISCOVER_TURN_TEXT,
          failureUxOwner: 'caller',
        }).accepted.catch(() => undefined),
    });
    return () => setBridge(null);
  }, [
    ready,
    isSubmitting,
    turnInFlight,
    workspaceId,
    sessionId,
    noteRevision,
    projectId,
    enqueueTurn,
    setBridge,
    setApplyStatus,
    onStaleNoteRevision,
    trackArtifactApplied,
    intl,
  ]);
};

/** `work_artifact_actioned` for an accepted apply, with the Work billing columns. */
const trackArtifactApplied: PublishNotePushApplyHost['trackArtifactApplied'] = (
  props
) => {
  const shell = getWorkHostPort('shell');
  shell.trackWorkEvent(shell.workLaunchEventName.WorkArtifactActioned, {
    ...props,
    ...shell.resolveWorkBillingAnalyticsProps(),
  });
};

// The binding calls the core through this object so a suite can stand in for it.
export const publishNotePushApply = {
  useCore: usePublishNotePushApplyCore,
};

/**
 * Publish the note-push apply bridge while the chat is mounted, bound to the
 * app: the ambient workspace, the copilot escalation's session for the run, the
 * note view's revision, the note re-fetch and the Work emit.
 */
export const usePublishNotePushApply = (
  params: Parameters<typeof usePublishNotePushApplyCore>[0]
): void => {
  const queryClient = useQueryClient();
  const workspaceId = useWorkspaceId();
  const runContextByProject = getWorkHostPort('desktop').useCopilotRunContextByProject();
  const sessionId = runContextByProject[params.projectId]?.sessionId ?? null;
  // The note view's opaque revision token, echoed VERBATIM into the plan so the
  // backend can fail-closed when the note changed under the confirmed mapping.
  // A READ-ONLY observer over the session view the main window already fetches
  // (the note view / active-note publisher own the fetch — `skipToken` never
  // fetches here): reactive, so the plan built at submit time carries the token
  // of the note version on screen, including after the stale-409 re-fetch below.
  const { data: noteRevision } = useQuery<
    QuerySessionResponse | undefined,
    Error,
    string | null
  >({
    queryKey: [GET_SESSION_BY_ID, { sessionId: sessionId ?? '' }],
    queryFn: skipToken,
    select: (session) => session?.consult_note?.updated_at ?? null,
  });
  const onStaleNoteRevision = useCallback(
    (staleSessionId: string) => {
      void queryClient.invalidateQueries({
        queryKey: [GET_SESSION_BY_ID, { sessionId: staleSessionId }],
      });
    },
    [queryClient]
  );

  publishNotePushApply.useCore(params, {
    workspaceId,
    sessionId,
    noteRevision,
    onStaleNoteRevision,
    trackArtifactApplied,
  });
};
